import * as React from "react";
import {WithApi, WithApiProperties} from "../../common/util/WithApi";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {Previewer, RESTRICT_NAVIGATION_BOUNDS} from "../../common/ui/riamap/Previewer";
import * as paths from "../../paths";
import {BackgroundDataInfo} from "../../common/ui/riamap/model";
import {Logger} from "../../common/util/Logger";
import {Alert} from "react-bootstrap/lib";
import {ChangeBackgroundDataButton} from "./ChangeBackgroundDataButton";
import {RevertBackgroundDataButton} from "./RevertBackgroundDataButton";
import {Button, Col, ControlLabel, Form, FormControl, FormGroup, OverlayTrigger, Row, Tooltip} from "react-bootstrap";
import {ApolloApi} from "../../api/ApolloApi";
import {ActionBar} from "../../common/ui/actionbar/ActionBar";
import {Spinner} from "../../common/ui/spinner/Spinner";
import {mapErrorToMessage} from "../../common/util/ErrorMapper";
import {ErrorDisplay} from "../../common/ui/errordisplay/ErrorDisplay";
import {getReference} from "@luciad/ria/reference/ReferenceProvider.js";
import {
  localRepository,
  PREVIEW_BACKGROUND_TYPE,
  PREVIEW_BACKGROUND_BOUNDS,
  PREVIEW_BACKGROUND_CRS,
  PREVIEW_BACKGROUND_URL,
  PREVIEW_BACKGROUND_SUBDOMAINS,
  PREVIEW_BACKGROUND_LAYERNAME
} from "../../common/LocalRepository";
import {ConfigurationSet} from "../model";
import {Redirect} from "react-router-dom";
import {AxiosError} from "axios";

const LABEL_COL = 2;
const CONTROL_COL = 7;
const HELP_COL = 3;
const CONFIG_PAGE_MESSAGES = defineMessages({
  HELP: {
    id: "studio.UI.help-alt",
    defaultMessage: "❔",
  },
  LOCAL: {
    id: "studio.settings.preview-background.type-local",
    defaultMessage: "Local File"
  },
  WMS: {
    id: "studio.settings.preview-background.type-wms",
    defaultMessage: "WMS"
  },
  URLTILESET: {
    id: "studio.settings.preview-background.type-tile-set",
    defaultMessage: "URL Tile Set"
  },
  UNSUPPORTEDCRS: {
    id: "studio.settings.preview-background.Unsupported-crs",
    defaultMessage: "Unsupported CRS"
  },
  BOUNDSERROR: {
    id: "studio.settings.preview-background.bounds-param-count",
    defaultMessage: "All values must be entered or empty."
  },
  YHEIGHTERROR: {
    id: "studio.settings.preview-background.y-height-error",
    defaultMessage: "(Y + Height) must be <= 90"
  }
});

interface PreviewerPageState {
  loading: boolean;
  saving: boolean;
  error: AxiosError;
  crsError: string;
  loadingFailed: boolean;
  backgroundDataInfo: BackgroundDataInfo;
  backgroundDataSequenceNumber: number;
  previewBackgroundType: string;
  previewBackgroundBoundsX: string;
  previewBackgroundBoundsWidth: string;
  previewBackgroundBoundsY: string;
  previewBackgroundBoundsHeight: string;
  boundsError: string;
  previewBackgroundCrs: string;
  previewBackgroundUrl: string;
  previewBackgroundLayername: string;
  previewBackgroundSubdomains: string;
  configuration: ConfigurationSet[];
}

class PreviewerPageComponent extends React.Component<WithApiProperties & InjectedIntlProps, PreviewerPageState> {

  _logger: Logger = Logger.getLogger("settings.MapPreviewPage");

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      error: null,
      crsError: null,
      loadingFailed: false,
      backgroundDataInfo: null,
      backgroundDataSequenceNumber: 1,
      previewBackgroundType: "local",
      previewBackgroundBoundsX: "",
      previewBackgroundBoundsWidth: "",
      previewBackgroundBoundsY: "",
      previewBackgroundBoundsHeight: "",
      boundsError: null,
      previewBackgroundCrs: "",
      previewBackgroundUrl: "",
      previewBackgroundLayername: "",
      previewBackgroundSubdomains: "",
      configuration: null
    }
  }

  componentDidMount() {
    const settings = [];

    settings.push(ApolloApi.getStudioSettingsDescription());
    settings.push(ApolloApi.getStudioSettings());
    Promise.allSettled(settings).then(((response) => {
      const rejected = response.filter((res) =>
          res.status === 'rejected' && res?.reason?.response.status === 401) as PromiseRejectedResult[];
      if (rejected.length > 0) {
        this.setState({error: rejected[0]?.reason});
        return;
      }
      const results = response.filter((res) => res.status === 'fulfilled') as PromiseFulfilledResult<any>[];
      const configuration = results[0].value.configurationSets;
      const {
        previewBackgroundType,
        previewBackgroundBounds,
        previewBackgroundCrs,
        previewBackgroundUrl,
        previewBackgroundLayername,
        previewBackgroundSubdomains
      } = results[1].value.data.values;

      let previewBackgroundBoundsX = RESTRICT_NAVIGATION_BOUNDS.x.toString();
      let previewBackgroundBoundsWidth = RESTRICT_NAVIGATION_BOUNDS.width.toString();
      let previewBackgroundBoundsY = RESTRICT_NAVIGATION_BOUNDS.y.toString();
      let previewBackgroundBoundsHeight = RESTRICT_NAVIGATION_BOUNDS.height.toString();

      if (previewBackgroundBounds) {
        const boundsA = previewBackgroundBounds.split(',');
        if (boundsA.length === 4) {
          previewBackgroundBoundsX = boundsA[0];
          previewBackgroundBoundsWidth = boundsA[1];
          previewBackgroundBoundsY = boundsA[2];
          previewBackgroundBoundsHeight = boundsA[3];
        }
      }

      this.setState(prevState => ({
        previewBackgroundType: previewBackgroundType || prevState.previewBackgroundType,
        previewBackgroundBoundsX,
        previewBackgroundBoundsWidth,
        previewBackgroundBoundsY,
        previewBackgroundBoundsHeight,
        previewBackgroundCrs: previewBackgroundCrs || prevState.previewBackgroundCrs,
        previewBackgroundUrl: previewBackgroundUrl || prevState.previewBackgroundUrl,
        previewBackgroundLayername: previewBackgroundLayername || prevState.previewBackgroundLayername,
        previewBackgroundSubdomains: previewBackgroundSubdomains || prevState.previewBackgroundSubdomains,
        configuration
      }));
      this.updateBackgroundDataInfo();

    })).catch((error) => {
      this.setState({error});
      throw error;
    });
  }

  updateBackgroundDataInfo = () => {
    this.props.api.getBackgroundDataInfo().then((backgroundDataInfo) => {
      this.setState(prevState => ({
        loading: false,
        loadingFailed: false,
        backgroundDataInfo,
        backgroundDataSequenceNumber: (prevState.backgroundDataSequenceNumber + 1)
      }));
    }).catch((error) => {
      this._logger.error("Error occurred while requesting background data info", error);
      this.setState({loading: false, loadingFailed: true});
    });
  }

  renderhelp(parameter: string) {
    const {intl} = this.props;

    return <Col sm={HELP_COL} className={"config-help-icon"}>
      <OverlayTrigger placement="top"
                      overlay={<Tooltip id="tooltip_help">{this.getDescription(parameter)}</Tooltip>}>
        <img src={`theme/assets/images/help_16.png`}
             alt={intl.formatMessage(CONFIG_PAGE_MESSAGES.HELP)}/>
      </OverlayTrigger>
      {parameter === "previewBackgroundCrs" &&
       <span style={{marginLeft: "10px", color: "red"}}> {this.state.crsError}</span>}
      {parameter === "previewBackgroundBounds" &&
       <span style={{marginLeft: "10px", color: "red"}}> {this.state.boundsError}</span>}
    </Col>
  }

  renderTypeSelector() {
    const {intl} = this.props;

    return <FormGroup controlId="previewBackgroundType">
      <Col componentClass={ControlLabel} sm={LABEL_COL}>
        <FormattedMessage id="studio.settings.preview-background.type"
                          defaultMessage="Type"/>
      </Col>
      <Col sm={CONTROL_COL}>
        <FormControl name="previewBackgroundType" value={this.state.previewBackgroundType}
                     componentClass="select" onChange={this.onChange}>
          <option value="local">
            {intl.formatMessage(CONFIG_PAGE_MESSAGES.LOCAL)}
          </option>
          <option value="wms">
            {intl.formatMessage(CONFIG_PAGE_MESSAGES.WMS)}
          </option>
          <option value="urlTileSet">
            {intl.formatMessage(CONFIG_PAGE_MESSAGES.URLTILESET)}
          </option>
        </FormControl>
      </Col>
      {this.renderhelp("previewBackgroundType")}
    </FormGroup>
  }

  renderCrsSelector() {
    return <FormGroup controlId="previewBackgroundCrs">
      <Col componentClass={ControlLabel} sm={LABEL_COL}>
        <FormattedMessage id="studio.settings.preview-background.crs"
                          defaultMessage="CRS Override"/>
      </Col>
      <Col sm={CONTROL_COL}>
        <FormControl name="previewBackgroundCrs" value={this.state.previewBackgroundCrs}
                     onChange={this.onChange}>
        </FormControl>
      </Col>
      {this.renderhelp("previewBackgroundCrs")}
    </FormGroup>
  }

  renderBoundsSelector() {
    const {
      previewBackgroundBoundsX,
      previewBackgroundBoundsWidth,
      previewBackgroundBoundsY,
      previewBackgroundBoundsHeight
    } = this.state;
    return <div>
      <FormGroup controlId="previewBackgroundBounds">
        <Col componentClass={ControlLabel} sm={LABEL_COL}>
          <FormattedMessage id="studio.settings.preview-background.bounds"
                            defaultMessage="Limit Bounds"/>
        </Col>

        <Col sm={CONTROL_COL}>
          <div style={{width: "22%", marginRight: "4%", display: "inline-block"}}>
            <strong>
              <FormattedMessage id="studio.settings.preview-background.bounds-x" defaultMessage="X"/>
            </strong>
            <FormControl name="previewBackgroundBoundsX"
                         type="number" min={-180} max={180}
                         value={previewBackgroundBoundsX}
                         onChange={this.onChange}>
            </FormControl>
          </div>

          <div style={{width: "22%", marginRight: "4%", display: "inline-block"}}>
            <strong>
              <FormattedMessage id="studio.settings.preview-background.bounds-width" defaultMessage="Width"/>
            </strong>

            <FormControl name="previewBackgroundBoundsWidth"
                         type="number" min={0} max={360}
                         value={previewBackgroundBoundsWidth}
                         onChange={this.onChange}>
            </FormControl>
          </div>

          <div style={{width: "22%", marginRight: "4%", display: "inline-block"}}>
            <strong>
              <FormattedMessage id="studio.settings.preview-background.bounds-y" defaultMessage="Y"/>
            </strong>
            <FormControl name="previewBackgroundBoundsY"
                         type="number" min={-90} max={90}
                         value={previewBackgroundBoundsY}
                         onChange={this.onChange}>
            </FormControl>
          </div>

          <div style={{width: "22%", display: "inline-block"}}>
            <strong>
              <FormattedMessage id="studio.settings.preview-background.bounds-height"
                                defaultMessage="Height"/>
            </strong>
            <FormControl name="previewBackgroundBoundsHeight"
                         type="number" min={-180} max={180}
                         value={previewBackgroundBoundsHeight}
                         onChange={this.onChange}>
            </FormControl>
          </div>

        </Col>
        {this.renderhelp("previewBackgroundBounds")}
      </FormGroup>
    </div>
  }

  private changeType(event: any) {
    const {value} = event.target;

    let previewBackgroundBoundsX = RESTRICT_NAVIGATION_BOUNDS.x.toString();
    let previewBackgroundBoundsWidth = RESTRICT_NAVIGATION_BOUNDS.width.toString();
    let previewBackgroundBoundsY = RESTRICT_NAVIGATION_BOUNDS.y.toString();
    let previewBackgroundBoundsHeight = RESTRICT_NAVIGATION_BOUNDS.height.toString();

    if (localRepository && value === localRepository.getParameter(PREVIEW_BACKGROUND_TYPE)) {
      const boundsA = localRepository.getParameter(PREVIEW_BACKGROUND_BOUNDS).split(',');
      if (boundsA.length === 4) {
        previewBackgroundBoundsX = boundsA[0];
        previewBackgroundBoundsWidth = boundsA[1];
        previewBackgroundBoundsY = boundsA[2];
        previewBackgroundBoundsHeight = boundsA[3];
      }
      this.setState({
        previewBackgroundType: value,
        previewBackgroundCrs: localRepository.getParameter(PREVIEW_BACKGROUND_CRS),
        previewBackgroundBoundsX,
        previewBackgroundBoundsWidth,
        previewBackgroundBoundsY,
        previewBackgroundBoundsHeight,
        previewBackgroundUrl: localRepository.getParameter(PREVIEW_BACKGROUND_URL),
        previewBackgroundLayername: localRepository.getParameter(PREVIEW_BACKGROUND_LAYERNAME),
        previewBackgroundSubdomains: localRepository.getParameter(PREVIEW_BACKGROUND_SUBDOMAINS),
      });
      return;
    }
    this.setState({
      previewBackgroundType: value,
      previewBackgroundBoundsX,
      previewBackgroundBoundsWidth,
      previewBackgroundBoundsY,
      previewBackgroundBoundsHeight,
      previewBackgroundCrs: "",
      previewBackgroundUrl: "",
      previewBackgroundLayername: "",
      previewBackgroundSubdomains: "",
    });
  }

  private changeUrl(event: any) {
    const {name, value} = event.target;
    const previewBackgroundSubdomains = value.includes("{s}") ?
                                        localRepository.getParameter(PREVIEW_BACKGROUND_SUBDOMAINS) : "";
    this.setState({
      ...this.state,
      previewBackgroundSubdomains,
      [name]: value
    });
  }

  private changeCrs(event: any) {
    const {intl} = this.props;
    const {value} = event.target;

    const previewBackgroundCrs = value.toUpperCase()
    try {
      if (previewBackgroundCrs && previewBackgroundCrs !== "") {
        getReference(previewBackgroundCrs);
      }
      this.setState({
        previewBackgroundCrs,
        crsError: null
      });
    } catch (error) {
      this.setState({
        previewBackgroundCrs,
        crsError: intl.formatMessage(CONFIG_PAGE_MESSAGES.UNSUPPORTEDCRS)
      });
    }
  }

  private changeBounds(event: any) {
    const {intl} = this.props;
    const {name, min, max} = event.target;
    let {value} = event.target;
    let boundsError = null;

    if (Number(value) > Number(max)) {
      value = max;
    } else if (Number(value) < Number(min)) {
      value = min;
    }

    const {
      previewBackgroundBoundsX,
      previewBackgroundBoundsWidth,
      previewBackgroundBoundsY,
      previewBackgroundBoundsHeight
    } = this.state;
    const bounds = {
      previewBackgroundBoundsX,
      previewBackgroundBoundsWidth,
      previewBackgroundBoundsY,
      previewBackgroundBoundsHeight
    };
    bounds[name] = value;
    const count = (Object.values(bounds)).filter((str) => str === "" || str === null).length;
    if (count === 4) {
      this.setState({
        boundsError: null,
        previewBackgroundBoundsX: RESTRICT_NAVIGATION_BOUNDS.x.toString(),
        previewBackgroundBoundsWidth: RESTRICT_NAVIGATION_BOUNDS.width.toString(),
        previewBackgroundBoundsY: RESTRICT_NAVIGATION_BOUNDS.y.toString(),
        previewBackgroundBoundsHeight: RESTRICT_NAVIGATION_BOUNDS.height.toString()
      });
      return;
    }
    if (count > 0 && count < 4) {
      boundsError = intl.formatMessage(CONFIG_PAGE_MESSAGES.BOUNDSERROR);
    } else if (Number(bounds["previewBackgroundBoundsY"]) + Number(bounds["previewBackgroundBoundsHeight"]) > 90) {
      boundsError = intl.formatMessage(CONFIG_PAGE_MESSAGES.YHEIGHTERROR);
    }

    this.setState({
      ...this.state,
      boundsError,
      [name]: value
    });
  }

  private onChange = (event: any) => {
    const {name, value} = event.target;

    switch (name) {
    case "previewBackgroundType":
      this.changeType(event);
      return;
    case "previewBackgroundUrl":
      this.changeUrl(event);
      return;
    case "previewBackgroundCrs":
      this.changeCrs(event);
      return;
    }

    if (name.startsWith("previewBackgroundBounds")) {
      this.changeBounds(event);
      return;
    }

    this.setState({
      ...this.state,
      [name]: value
    });

  }

  renderLocalFileSelection() {
    const {backgroundDataInfo} = this.state;
    return <FormGroup>
      <Col componentClass={ControlLabel} sm={LABEL_COL}>
        <FormattedMessage id="studio.settings.map-preview.background.local-file"
                          defaultMessage="Local File"/>
      </Col>
      <Col sm={CONTROL_COL}>
        <div className="backgroundData" style={{width: "100%", textAlign: "center"}}>
          <div className="backgroundDataFilePath">
            {backgroundDataInfo.currentFilePath ?
             backgroundDataInfo.currentFilePath :
             <FormattedMessage id="studio.settings.map-preview.background-data.not-set"
                               defaultMessage="Not set"/>}
          </div>
          <div className="backgroundButtons"
               style={{display: "block", marginLeft: "auto", marginRight: "auto"}}>
            <div style={{display: "inline-flex"}}>
              <ChangeBackgroundDataButton onSuccess={this.updateBackgroundDataInfo}/>
              {backgroundDataInfo.defaultFilePath ?
               <RevertBackgroundDataButton defaultBackgroundData={backgroundDataInfo.defaultFilePath}
                                           onSuccess={this.updateBackgroundDataInfo}/> : null}
            </div>
          </div>
        </div>
      </Col>
    </FormGroup>
  }

  private getDescription = (parameter: string) => {
    const {configuration} = this.state;
    const mapPreview = configuration.find(({name}) => name === "Map Preview");
    const parameterDetails = mapPreview ? mapPreview.details.find(
        ({configurationItemKey}) => configurationItemKey === parameter
    ) : null;
    return parameterDetails ? parameterDetails.description : null;
  }

  renderOtherSelection() {
    const {
      previewBackgroundUrl,
      previewBackgroundType,
      previewBackgroundLayername,
      previewBackgroundSubdomains
    } = this.state;

    return <FormGroup>
      <FormGroup controlId="previewBackgroundUrl">
        <Col componentClass={ControlLabel} sm={LABEL_COL}>
          <FormattedMessage id="studio.settings.preview-background.url"
                            defaultMessage="URL"/>
        </Col>
        <Col sm={CONTROL_COL}>
          <FormControl name="previewBackgroundUrl" value={previewBackgroundUrl}
                       onChange={this.onChange}/>
        </Col>
        {this.renderhelp("previewBackgroundUrl")}
      </FormGroup>

      <FormGroup controlId="previewBackgroundLayername"
                 hidden={previewBackgroundType !== "wms"}>
        <Col componentClass={ControlLabel} sm={LABEL_COL}>
          <FormattedMessage id="studio.settings.preview-background.layername"
                            defaultMessage="Layer Name"/>
        </Col>
        <Col sm={CONTROL_COL}>
          <FormControl name="previewBackgroundLayername" value={previewBackgroundLayername}
                       onChange={this.onChange}/>
        </Col>
        {this.renderhelp("previewBackgroundLayername")}
      </FormGroup>

      <FormGroup controlId="previewBackgroundSubdomains"
                 hidden={previewBackgroundType !== "urlTileSet"}>
        <Col componentClass={ControlLabel} sm={LABEL_COL}>
          <FormattedMessage id="studio.settings.preview-background.subdomains"
                            defaultMessage="Subdomains"/>
        </Col>
        <Col sm={CONTROL_COL}>
          <FormControl name="previewBackgroundSubdomains" value={previewBackgroundSubdomains}
                       onChange={this.onChange}
                       disabled={previewBackgroundType !== "urlTileSet" ||
                                 previewBackgroundUrl === null ||
                                 !previewBackgroundUrl.includes("{s}")}/>
        </Col>
        {this.renderhelp("previewBackgroundSubdomains")}
      </FormGroup>
    </FormGroup>
  }

  private save = () => {
    const {
      previewBackgroundType,
      previewBackgroundBoundsX,
      previewBackgroundBoundsWidth,
      previewBackgroundBoundsY,
      previewBackgroundBoundsHeight,
      previewBackgroundCrs,
      crsError,
      boundsError,
      previewBackgroundUrl,
      previewBackgroundLayername,
      previewBackgroundSubdomains
    } = this.state;

    let previewBackgroundBounds = `${RESTRICT_NAVIGATION_BOUNDS.x},${RESTRICT_NAVIGATION_BOUNDS.width},${RESTRICT_NAVIGATION_BOUNDS.y},${RESTRICT_NAVIGATION_BOUNDS.height}`;
    if (!boundsError) {
      previewBackgroundBounds
          = `${previewBackgroundBoundsX},${previewBackgroundBoundsWidth},${previewBackgroundBoundsY},${previewBackgroundBoundsHeight}`;
    }

    this.setState({
      previewBackgroundCrs: crsError ? "" : previewBackgroundCrs,
      previewBackgroundBoundsX: boundsError ? RESTRICT_NAVIGATION_BOUNDS.x.toString() : previewBackgroundBoundsX,
      previewBackgroundBoundsWidth: boundsError ? RESTRICT_NAVIGATION_BOUNDS.width.toString()
                                                : previewBackgroundBoundsWidth,
      previewBackgroundBoundsY: boundsError ? RESTRICT_NAVIGATION_BOUNDS.y.toString() : previewBackgroundBoundsY,
      previewBackgroundBoundsHeight: boundsError ? RESTRICT_NAVIGATION_BOUNDS.height.toString()
                                                 : previewBackgroundBoundsHeight,
      crsError: null,
      boundsError: null,
      saving: true
    });
    localRepository.setParameter(PREVIEW_BACKGROUND_TYPE, previewBackgroundType);
    localRepository.setParameter(PREVIEW_BACKGROUND_BOUNDS, previewBackgroundBounds);
    localRepository.setParameter(PREVIEW_BACKGROUND_CRS, crsError ? "" : previewBackgroundCrs);
    localRepository.setParameter(PREVIEW_BACKGROUND_URL, previewBackgroundUrl);
    localRepository.setParameter(PREVIEW_BACKGROUND_SUBDOMAINS, previewBackgroundSubdomains);
    localRepository.setParameter(PREVIEW_BACKGROUND_LAYERNAME, previewBackgroundLayername);

    ApolloApi.saveStudioSettings({
      values: {
        previewBackgroundType,
        previewBackgroundBounds,
        previewBackgroundCrs: crsError ? "" : previewBackgroundCrs,
        previewBackgroundUrl,
        previewBackgroundLayername,
        previewBackgroundSubdomains
      }
    })
        .then(() => {
          this.setState({saving: false});
          this.updateBackgroundDataInfo();
        })
        .catch((error) => {
          this.setState({saving: false, error});
          throw error;
        })
        .then(() => {
          this.setState({saving: false});
          this.updateBackgroundDataInfo();
        });
  }

  renderBackgroundDataInfo() {
    const {loadingFailed, backgroundDataInfo, backgroundDataSequenceNumber, previewBackgroundType} = this.state;
    const {api} = this.props;
    if (loadingFailed) {
      return <Alert bsStyle="danger">
        <FormattedMessage id="studio.settings.map-preview.background-data.loading-failed"
                          defaultMessage="Failed to load background data"/>
      </Alert>
    }
    return (
        <Form horizontal className="settings-form">
          {this.renderTypeSelector()}
          {previewBackgroundType === "local" && this.renderLocalFileSelection()}
          {previewBackgroundType !== "local" && this.renderOtherSelection()}
          {this.renderCrsSelector()}
          {this.renderBoundsSelector()}
          <FormGroup>
            <Col sm={LABEL_COL + CONTROL_COL}>
              <ActionBar>
                <Button onClick={this.save}>
                  <FormattedMessage id="studio.settings.general.save" defaultMessage="Save"/>
                </Button>
              </ActionBar>
            </Col>
          </FormGroup>

          {backgroundDataInfo.currentFilePath ?
           <div className="backgroundPreviewer">
             <Previewer wmsBaseUrl={paths.PREVIEW_DATA_PATH}
                        background={api.getPreviewBackground()}
                        dataToVisualize={[]}
                        shouldFit={true}
                        backgroundDataPreview={true}
                        backgroundSequenceNumber={backgroundDataSequenceNumber}
                        hideFitDataButton={true} height="400px"/>
           </div> : null}
        </Form>
    );
  }

  private renderSaving() {
    return (
        <Row style={{textAlign: "center"}}>
          <h2>
            <FormattedMessage id="studio.settings.general.saving"
                              defaultMessage="Saving..."/>
          </h2>
          <Spinner bigStyle/>
        </Row>
    );
  }

  private renderLoading() {
    return (
        <Row style={{textAlign: "center"}}>
          <h2>
            <FormattedMessage id="studio.settings.general.loading"
                              defaultMessage="Loading..."/>
          </h2>
          <Spinner bigStyle/>
        </Row>
    );
  }

  private renderError() {
    const {error} = this.state;
    const errorMessage = mapErrorToMessage(error);
    if (errorMessage) {
      return <Alert bsStyle="danger">{errorMessage as string}</Alert>;
    }
    return <ErrorDisplay error={error}/>;
  }

  render() {
    const {loading, saving, error} = this.state;
    return (
        <div>
          <h1><FormattedMessage id="studio.settings.map-preview.header" defaultMessage="Map preview"/></h1>
          <hr/>
          <h3><FormattedMessage id="studio.settings.map-preview.background-data.header"
                                defaultMessage="Background data"/>
          </h3>
          <p>
            <FormattedMessage id="studio.settings.map-preview.background-data.description"
                              defaultMessage="Configure the data that is used as background imagery in the map preview."/>
          </p>

          {loading && this.renderLoading()}
          {saving && this.renderSaving()}
          {error?.response.status === 401 && <Redirect to="/login"/>}
          {error && this.renderError()}
          {loading ? null : this.renderBackgroundDataInfo()}
        </div>
    );
  }
}

export const
    MapPreviewPage = injectIntl(WithApi(PreviewerPageComponent));